import Modal from '../../common/Modal';
import Card from '../../common/Card';
import SelectSearch from '../../common/SelectSearch';
import {
    createCompetitorChainOptions,
    createCompetitorStoreOptionsWithoutSelected,
    createStoreOptions,
} from '../../../utils/helpers/option';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchBusinessFormats,
    fetchCompetitorChains,
    fetchCompetitorStores,
    fetchDistances,
    fetchFrequencyRanks,
    fetchStores,
    setLoader,
} from '../../../redux/actions/api';
import { CompetitorMasterControllerService } from '../../../services';
import { setToastsError, setToastsSuccess } from '../../../redux/actions/common';
import apiErrorHandler from '../../../api/apiErrorHandler';
import { useLocation } from 'react-router-dom';
import { clearErrors } from '../../../redux/actions/error';
import Button from '../../common/Button';
import { RequiredBadge } from '../../common/RequiredBadge';

const DEFAULT_STATE = {
    selectedDistance: '',
    selectedRank: '',
    notificationFlag: '',

    apiResponse: null,
    selectedModalCompetitorChainCode: '',
    selectedModalOkStoreCode: '',
    selectedModalStoreCode: '',

    deleteIndex: null,
    isShowConfirmDeleteModal: false,
};

const DEFAULT_ROWS = [{ id: 1, storeCode: '', distance: '', rank: '', notificationFlag: false }];

const CompetitorCombinationMasterForStoreModal = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const { isShow, handleCloseModal } = props;

    const constants = useSelector((state) => state.constants);
    const { stores, competitorStores, competitorChains, distances, frequencyRanks } = constants;

    const [state, setState] = useState(DEFAULT_STATE);

    const [rows, setRows] = useState([]);

    const {
        apiResponse,
        selectedModalOkStoreCode,

        deleteIndex,
        isShowConfirmDeleteModal,
    } = state;

    useEffect(() => {
        dispatch(clearErrors());
        dispatch(fetchStores());
        dispatch(fetchCompetitorChains());
        dispatch(fetchCompetitorStores());
        dispatch(fetchDistances());
        dispatch(fetchFrequencyRanks());
        dispatch(fetchBusinessFormats());
    }, [dispatch, location]);

    const getCombinationCompetitorMaster = () => {
        dispatch(setLoader(true));
        dispatch(clearErrors());
        // オーケー店舗登録モーダルの競合店舗と店舗名を入れ替え
        CompetitorMasterControllerService.searchCompetitorCombinations(
            null,
            selectedModalOkStoreCode,
            null,
            null,
            1,
            1000, // ページング不要のため指定
        )
            .then((response) => {
                handleChangeState('apiResponse', response);
            })
            .catch((error) => {
                apiErrorHandler(dispatch, error);
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    useEffect(() => {
        if (selectedModalOkStoreCode) {
            getCombinationCompetitorMaster();
        }
    }, [selectedModalOkStoreCode]);

    const handleChangeState = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleChangeApiResponse = (index, field, value) => {
        const newApiResponseData = apiResponse.data;
        newApiResponseData[index][field] = value;
        handleChangeState('apiResponse', {
            ...apiResponse,
            data: newApiResponseData,
        });
    };

    const validation = () => {
        dispatch(clearErrors());
        if (!selectedModalOkStoreCode) {
            dispatch(setToastsError('オーケー店舗名を選択してください。'));
            return true;
        }
        const updateHasError = apiResponse.data.some((item) => {
            if (!item.distance) {
                dispatch(setToastsError('距離を指定してください。'));
                return true;
            }
            if (!item.storeRank) {
                dispatch(setToastsError('調査頻度を設定してください。'));
                return true;
            }
            return false;
        });

        if (updateHasError) {
            return true;
        }
        const createHasError = rows.some((item) => {
            if (!item.selectedModalCompetitorChainCode) {
                dispatch(setToastsError('競合チェーン名を選択してください。'));
                return true;
            }
            if (!item.competitorStoreCode) {
                dispatch(setToastsError('競合店舗名を選択してください。'));
                return true;
            }
            if (!item.distance) {
                dispatch(setToastsError('距離を指定してください。'));
                return true;
            }
            if (!item.rank) {
                dispatch(setToastsError('調査頻度を設定してください。'));
                return true;
            }
            return false;
        });

        if (createHasError) {
            return true;
        }
        return false;
    };

    const handleSubmit = () => {
        if (validation()) return;
        dispatch(setLoader(true));
        dispatch(clearErrors());
        const updateData = apiResponse.data?.map(({ id, distance, storeRank, deleteFlag, notificationFlag }) => ({
            id: id,
            distance: distance,
            rank: storeRank,
            deleteFlag: deleteFlag,
            notificationFlag: notificationFlag,
        }));

        const createData = rows?.map(({ id, ...rest }) => ({
            ...rest,
            storeCode: selectedModalOkStoreCode,
        }));

        const upsertData = [...updateData, ...createData];

        CompetitorMasterControllerService.upsertCompetitorCombinations({ combinationUpsertInfos: upsertData })
            .then(() => {
                dispatch(setToastsSuccess('競合店舗紐づけを更新しました。'));
                setState(DEFAULT_STATE);
                setRows(DEFAULT_ROWS);
                handleCloseModal();
            })
            .catch((error) => {
                apiErrorHandler(dispatch, error, t('message.error.update'));
            })
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const toggleDeleteCheckbox = (index) => {
        handleChangeState('deleteIndex', index);
        handleChangeState('isShowConfirmDeleteModal', true);
    };

    const handleDelete = () => {
        const deleteFlag = apiResponse.data[deleteIndex].deleteFlag;
        handleChangeApiResponse(deleteIndex, 'deleteFlag', !deleteFlag);
        handleChangeState('isShowConfirmDeleteModal', false);
    };

    const handleResetAndCloseModal = () => {
        setState(DEFAULT_STATE);
        setRows(DEFAULT_ROWS);
        handleCloseModal();
    };

    const handleChangeRow = (id, field, value) => {
        setRows((prevRows) => prevRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    };

    const handleAddRow = () => {
        setRows((prevRows) => [
            ...prevRows,
            {
                id: prevRows.length ? prevRows[prevRows.length - 1].id + 1 : 1,
                storeCode: '',
                distance: '',
                rank: '',
                notificationFlag: false,
            },
        ]);
    };

    const handleRemoveRow = (id) => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
    };

    return (
        <>
            <Modal
                size="xl"
                isShow={isShow}
                onClose={handleResetAndCloseModal}
                isShowIconClose="true"
                title={t('update_competitor_master.registration-modal.header')}
                btnList={[
                    {
                        name: t('update_competitor_master.registration-modal.footer.primary-btn'),
                        className: 'btn--primary',
                        onClick: () => handleSubmit(),
                    },
                    {
                        name: t('update_competitor_master.registration-modal.footer.secondary-btn'),
                        className: 'btn--secondary',
                        onClick: () => handleResetAndCloseModal(),
                    },
                ]}
            >
                <div className="okc-card okc-result-form">
                    <Card type="modal_card">
                        <div className="row okc-card__row">
                            <div className="col-lg-3 okc-result-form__title">
                                <p>
                                    オーケー店舗名
                                    <RequiredBadge />
                                </p>
                            </div>
                            <div className="col-lg-8 offset-lg-1 d-flex">
                                <div className="row w-100">
                                    <div className="col-12">
                                        <SelectSearch
                                            onChange={(option) => handleChangeState('selectedModalOkStoreCode', option.value)}
                                            options={createStoreOptions(stores)}
                                            value={selectedModalOkStoreCode}
                                            placeholder="オーケー店舗名を検索して選択してください"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {selectedModalOkStoreCode && (
                            <>
                                <div className="competitor-store-list-wrapper">
                                    <div className="competitor-store-header">
                                        <div className="row okc-card__row">
                                            <div className="col-lg-7 pl-2">
                                                <div className="col-lg-4">
                                                    {t('update_competitor_master.search_form.competitor_chain.text')}
                                                </div>
                                                <div className="col-lg-8">
                                                    {t('update_competitor_master.search_form.competitor_store.text')}
                                                </div>
                                            </div>
                                            <div className="col-lg-2">
                                                {t('update_competitor_master.registration-modal.body.store-list.distance')}
                                            </div>
                                            <div className="col-lg-1">
                                                {t('update_competitor_master.registration-modal.body.store-list.rank')}
                                            </div>
                                            <div className="col-lg-1 flex-column">
                                                {t('update_competitor_master.registration-modal.body.store-list.notification_flag')}
                                            </div>
                                            <div className="col-lg-1 flex-column">
                                                {t('update_competitor_master.registration-modal.body.store-list.delete_flag')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="">
                                        {apiResponse?.data.map((item, index) => (
                                            <div className="row okc-row">
                                                <div className="col-lg-7 pl-2">
                                                    <div className="col-lg-4">{item.competitorChainName}</div>
                                                    <div className="col-lg-8">{item.competitorStoreName}</div>
                                                </div>
                                                <div className="col-lg-2 pr-2">
                                                    <SelectSearch
                                                        onChange={(option) => {
                                                            handleChangeApiResponse(index, 'distance', option.value);
                                                        }}
                                                        options={distances}
                                                        value={item.distance}
                                                        placeholder={t(
                                                            'update_competitor_master.registration-modal.body.distance.placeholder',
                                                        )}
                                                    />
                                                </div>
                                                <div className="col-lg-1">
                                                    <SelectSearch
                                                        onChange={(option) => {
                                                            handleChangeApiResponse(index, 'storeRank', option.value);
                                                        }}
                                                        options={frequencyRanks}
                                                        value={item.storeRank}
                                                        placeholder={t('update_competitor_master.registration-modal.body.rank.placeholder')}
                                                    />
                                                </div>
                                                <div className="col-lg-1">
                                                    <div
                                                        key={index}
                                                        className={`form-check custom-checkbox`}
                                                        style={{
                                                            marginLeft: '22px',
                                                            width: '38px',
                                                            height: '38px',
                                                        }}
                                                    >
                                                        <input
                                                            className="form-check-input"
                                                            onChange={() =>
                                                                handleChangeApiResponse(index, 'notificationFlag', !item.notificationFlag)
                                                            }
                                                            type="checkbox"
                                                            id={`notificationFlag-${item.id}`}
                                                            checked={item.notificationFlag}
                                                        />
                                                        <label
                                                            className="form-check-label competitor-combination-master-modal"
                                                            htmlFor={`notificationFlag-${item.id}`}
                                                        ></label>
                                                    </div>
                                                </div>
                                                <div className="col-lg-1">
                                                    <div
                                                        key={index}
                                                        className={`form-check custom-checkbox`}
                                                        style={{
                                                            marginLeft: '22px',
                                                            width: '38px',
                                                            height: '38px',
                                                        }}
                                                    >
                                                        <input
                                                            className="form-check-input"
                                                            onChange={() => toggleDeleteCheckbox(index)}
                                                            type="checkbox"
                                                            id={`deleteFlag-${item.id}`}
                                                            checked={!item.deleteFlag}
                                                        />
                                                        <label
                                                            className="form-check-label competitor-combination-master-modal"
                                                            htmlFor={`deleteFlag-${item.id}`}
                                                        ></label>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="okc-card__row justify-content-space-evenly pt-3 m-0">
                                            {rows.map((row) => (
                                                <div key={row.id} className="d-flex align-items-center mb-3">
                                                    <div className="col-lg-7" style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                                                        <div className="col-lg-4" style={{ marginRight: '5px' }}>
                                                            <SelectSearch
                                                                onChange={(option) => {
                                                                    handleChangeRow(
                                                                        row.id,
                                                                        'selectedModalCompetitorChainCode',
                                                                        option.value,
                                                                    );
                                                                    handleChangeRow(row.id, 'competitorStoreCode', '');
                                                                }}
                                                                options={createCompetitorChainOptions(competitorChains)}
                                                                value={row.selectedModalCompetitorChainCode}
                                                                placeholder={t('price_request.competitor_name.placeholder')}
                                                            />
                                                        </div>
                                                        <div className="col-lg-8" style={{ paddingRight: '5px' }}>
                                                            <SelectSearch
                                                                onChange={(option) =>
                                                                    handleChangeRow(row.id, 'competitorStoreCode', option.value)
                                                                }
                                                                options={createCompetitorStoreOptionsWithoutSelected(
                                                                    competitorStores,
                                                                    row.selectedModalCompetitorChainCode,
                                                                    rows,
                                                                    row,
                                                                    apiResponse?.data,
                                                                )}
                                                                value={row.competitorStoreCode}
                                                                placeholder={t('price_request.competitor_store_name.placeholder')}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2" style={{ paddingRight: '5px' }}>
                                                        <SelectSearch
                                                            onChange={(option) => handleChangeRow(row.id, 'distance', option.value)}
                                                            options={distances}
                                                            value={row.distance}
                                                            placeholder={t(
                                                                'update_competitor_master.registration-modal.body.distance.placeholder',
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="col-lg-1">
                                                        <SelectSearch
                                                            onChange={(option) => handleChangeRow(row.id, 'rank', option.value)}
                                                            options={frequencyRanks}
                                                            value={row.rank}
                                                            placeholder={t(
                                                                'update_competitor_master.registration-modal.body.rank.placeholder',
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="col-lg-1">
                                                        <div
                                                            className={`form-check custom-checkbox`}
                                                            style={{
                                                                marginLeft: '22px',
                                                                width: '38px',
                                                                height: '38px',
                                                            }}
                                                        >
                                                            <input
                                                                className="form-check-input"
                                                                onChange={() =>
                                                                    handleChangeRow(row.id, 'notificationFlag', !row.notificationFlag)
                                                                }
                                                                type="checkbox"
                                                                id={`notificationFlag-${row.id}`}
                                                                value={row.notificationFlag}
                                                                checked={row.notificationFlag}
                                                            />
                                                            <label
                                                                className="form-check-label competitor-combination-master-modal"
                                                                htmlFor={`notificationFlag-${row.id}`}
                                                                style={{ width: '38px', height: '38px', padding: 0 }}
                                                            ></label>
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="btn btn--icon btn--secondary confirm-delete-modal"
                                                        onClick={() => handleRemoveRow(row.id)}
                                                        style={{ marginLeft: '22px' }}
                                                    >
                                                        <i className="icon-bin"></i>
                                                        <span className="btn-text"></span>
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="d-flex justify-content-center align-items-center fade-in mt-4">
                                                <Button className="btn btn--primary" onClick={handleAddRow}>
                                                    <span className="btn-text">追加</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                </div>
            </Modal>

            <Modal
                size="sm"
                isShow={isShowConfirmDeleteModal}
                onClose={() => handleChangeState('isShowConfirmDeleteModal', false)}
                isShowIconClose="true"
                title={t('update_competitor_master.delete-modal.header')}
                btnList={[
                    {
                        name: t('update_competitor_master.delete-modal.primary_btn'),
                        className: 'btn--primary',
                        onClick: () => handleDelete(),
                    },
                    {
                        name: t('update_competitor_master.delete-modal.secondary_btn'),
                        className: 'btn--secondary',
                        onClick: () => handleChangeState('isShowConfirmDeleteModal', false),
                    },
                ]}
            >
                <p className="text-center">削除、または復元しますか？</p>
            </Modal>
        </>
    );
};

export default CompetitorCombinationMasterForStoreModal;
